import { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AgentBase } from '../types/AgentBaseInterface';
import { Workflow } from '@/features/Workflows/types/workflow.interface';
import WorkflowEditor, { WorkflowEditorRef } from '@/features/Workflows/components/WorkflowEditor';
import { WorkflowBaseService } from "@/features/Workflows/services/WorkflowBaseService";

interface WorkflowSettingsProps {
  agent: AgentBase;
  onUpdate?: (hasChanges: boolean) => void;
  refreshKey?: number; // Key para forçar reload
  organizationId?: string;
  workspaceId?: string;
  saveRef?: React.MutableRefObject<WorkflowEditorRef | null>;
}

export const AgentWorkflow = ({
  agent,
  onUpdate,
  refreshKey,
  organizationId,
  workspaceId,
  saveRef
}: WorkflowSettingsProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadWorkflow();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.id, refreshKey]); // Recarrega quando refreshKey mudar

  const loadWorkflow = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar o workflow do agente pelo ID do agent base
      try {
        const workflows = await WorkflowBaseService.getByAgentBaseId(agent.id);
        
        if (workflows && workflows.length > 0) {
          setWorkflow(workflows[0] as unknown as Workflow);
        }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (apiError: any) {
        throw new Error(`Erro ao buscar workflow: ${apiError.message}`);
      }
    } catch (err) {
      setError(t('agents.workflow.error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!workflow) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography>{t('agents.workflow.noWorkflow')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 'calc((100vh - 200px) * 0.8)', mb: 0 }}>
      <WorkflowEditor 
        workflowId={workflow.id} 
        onWorkflowChange={loadWorkflow}
        onUpdate={onUpdate}
        organizationId={organizationId}
        workspaceId={workspaceId}
        saveRef={saveRef}
        refreshKey={refreshKey}
      />
    </Box>
  );
};

export default AgentWorkflow;
